import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    FaClock, FaCheck, FaTimes, FaUser, FaProjectDiagram,
    FaCalendarAlt, FaCheckCircle, FaExclamationCircle, FaFileContract, FaFilter,
    FaLayerGroup, FaList, FaMapMarkerAlt, FaFileDownload, FaTable, FaChevronDown, FaChevronUp, FaEdit
} from 'react-icons/fa';
import pb from '../../lib/pocketbase';
import styles from '../../pages/Account.module.css';
import { toast } from 'react-toastify';
import { formatLocalDate } from '../../utils/timesheetHelpers';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default Leaflet icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TabTimesheetAdmin = () => {
    const [timesheets, setTimesheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'rejected'
    const [viewMode, setViewMode] = useState('grouped'); // 'grouped', 'list', 'recap'
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [expandedRecap, setExpandedRecap] = useState(true);
    // Admin Correction States
    const [editingTs, setEditingTs] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [projects, setProjects] = useState([]);
    const [projectCoords, setProjectCoords] = useState({}); // { chantierId: {lat, lng} }

    const months = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const geocodeChantier = async (chantier) => {
        if (!chantier?.address || projectCoords[chantier.id]) return;
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(chantier.address)}`);
            const data = await res.json();
            if (data && data.length > 0) {
                setProjectCoords(prev => ({
                    ...prev,
                    [chantier.id]: { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
                }));
            }
        } catch (err) {
            console.error("Geocoding mini error", err);
        }
    };

    const fetchAllTimesheets = async () => {
        try {
            setLoading(true);

            // Fetch projects for the correction modal
            const projs = await pb.collection('chantiers').getFullList({ sort: 'name' });
            setProjects(projs);

            // Trigger geocoding for each project (to calculate distances later)
            projs.forEach(p => geocodeChantier(p));

            // Calculate start and end of selected month
            const startDate = formatLocalDate(new Date(selectedYear, selectedMonth, 1));
            const endDate = formatLocalDate(new Date(selectedYear, selectedMonth + 1, 0));

            let filterQuery = `date >= "${startDate}" && date <= "${endDate}"`;

            if (filter === 'pending') {
                filterQuery += " && (status = 'pending_manager' || status = 'pending_rh' || status = 'litige' || status = '' || status = null)";
            } else if (filter === 'approved') {
                filterQuery += " && status = 'approved'";
            } else if (filter === 'rejected') {
                filterQuery += " && status = 'rejected'";
            } else if (filter === 'litige') {
                filterQuery += " && status = 'litige'";
            }

            const records = await pb.collection('timesheets').getFullList({
                filter: filterQuery,
                sort: '-date',
                expand: 'worker,chantier'
            });

            setTimesheets(records);
        } catch (error) {
            console.error("Error fetching timesheets:", error);
            // toast.error("Erreur lors de la récupération des heures.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllTimesheets();
        const unsubscribe = pb.collection('timesheets').subscribe('*', () => fetchAllTimesheets()).catch(() => { });
        return () => { pb.collection('timesheets').unsubscribe('*').catch(() => { }); };
    }, [filter, selectedMonth, selectedYear]);

    const handleStatusUpdate = async (itemOrId, newStatus) => {
        try {
            if (typeof itemOrId === 'string') {
                await pb.collection('timesheets').update(itemOrId, { status: newStatus });
            } else if (itemOrId.items) {
                // Bulk update for a grouped day
                await Promise.all(itemOrId.items.map(it => pb.collection('timesheets').update(it.id, { status: newStatus })));
            } else {
                await pb.collection('timesheets').update(itemOrId.id, { status: newStatus });
            }
            toast.success(newStatus === 'approved' ? 'Heures validées !' : 'Heures rejetées.');
            fetchAllTimesheets();
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Erreur lors de la mise à jour.");
        }
    };

    const handleBulkApprove = async (workerId) => {
        const workerSheets = timesheets.filter(t => t.worker === workerId && (t.status === 'pending_manager' || t.status === 'pending_rh' || !t.status));
        if (workerSheets.length === 0) return;

        if (!window.confirm(`Valider ${workerSheets.length} fiches pour cet ouvrier pour la période sélectionnée ?`)) return;

        try {
            await Promise.all(workerSheets.map(t => pb.collection('timesheets').update(t.id, { status: 'approved' })));
            toast.success(`${workerSheets.length} fiches validées !`);
            fetchAllTimesheets();
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la validation en masse.");
        }
    };

    // --- Correction Logic ---
    const openEditModal = (ts) => {
        setEditingTs({ ...ts });
        setIsEditModalOpen(true);
    };

    const handleSaveCorrection = async () => {
        if (!editingTs) return;
        setIsSaving(true);
        try {
            const data = {
                hours: parseFloat(editingTs.hours),
                morning_start: editingTs.morning_start,
                morning_end: editingTs.morning_end,
                afternoon_start: editingTs.afternoon_start,
                afternoon_end: editingTs.afternoon_end,
                chantier: editingTs.chantier,
                description: editingTs.description,
                status: 'approved', // Auto-approve on admin correction usually
                is_corrected: true,
                correction_author: pb.authStore.model?.id
            };
            await pb.collection('timesheets').update(editingTs.id, data);
            toast.success("Fiche corrigée et validée !");
            setIsEditModalOpen(false);
            fetchAllTimesheets();
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la correction.");
        } finally {
            setIsSaving(false);
        }
    };

    // Advanced Data Processing
    const payrollStats = useMemo(() => {
        const stats = {};
        timesheets.forEach(ts => {
            const workerId = ts.worker;
            const workerName = ts.expand?.worker?.name || 'Inconnu';
            const date = ts.date?.substring(0, 10);

            if (!stats[workerId]) {
                stats[workerId] = {
                    id: workerId,
                    name: workerName,
                    totalHours: 0,
                    daysWorked: new Set(),
                    overtime: 0,
                    paniers: 0,
                    items: [],
                    dailyGroups: {}, // Grouped by day
                    avatar: ts.expand?.worker?.avatar ? pb.files.getURL(ts.expand.worker, ts.expand.worker.avatar) : null
                };
            }

            stats[workerId].totalHours += (parseFloat(ts.hours) || 0);
            stats[workerId].daysWorked.add(date);
            stats[workerId].items.push(ts);

            // Grouping by day for the "One line per day" requirement
            if (!stats[workerId].dailyGroups[date]) {
                stats[workerId].dailyGroups[date] = {
                    id: ts.id,
                    date: date,
                    hours: 0,
                    status: ts.status || 'pending_manager',
                    chantier: ts.chantier,
                    description: '',
                    morning_start: ts.morning_start,
                    morning_end: ts.morning_end,
                    afternoon_start: ts.afternoon_start,
                    afternoon_end: ts.afternoon_end,
                    expand: ts.expand,
                    items: [],
                    is_corrected: ts.is_corrected
                };
            }

            const group = stats[workerId].dailyGroups[date];
            group.hours = parseFloat((group.hours + (parseFloat(ts.hours) || 0)).toFixed(1));
            group.items.push(ts);

            // Status Priority: Merge status of multiple entries into one daily status
            const statusPriority = { 'litige': 5, 'rejected': 4, 'pending_rh': 3, 'pending_manager': 2, 'approved': 1, '': 2, null: 2 };
            if (statusPriority[ts.status] > statusPriority[group.status]) {
                group.status = ts.status || 'pending_manager';
            }

            // Description merge
            if (ts.description && !group.description.includes(ts.description)) {
                if (!ts.description.includes('Session')) { // Avoid cluttering with session system messages
                    group.description += (group.description ? ' | ' : '') + ts.description;
                }
            }

            // Corrected flag: if any part of the day is corrected
            if (ts.is_corrected) group.is_corrected = true;
        });

        // Finalize dailyGroups into sorted arrays
        Object.values(stats).forEach(s => {
            s.paniers = s.daysWorked.size;
            const weeksInMonth = 4.33;
            const baseHours = 35 * weeksInMonth;
            s.overtime = Math.max(0, s.totalHours - baseHours).toFixed(1);
            s.totalHours = parseFloat(s.totalHours).toFixed(1);

            s.dailyItems = Object.values(s.dailyGroups).sort((a, b) => b.date.localeCompare(a.date));
        });

        return stats;
    }, [timesheets]);

    const exportRecapToCSV = () => {
        const headers = ["Employé", "Heures Totales", "Paniers Repas", "Heures Sup (est.)"];
        const rows = Object.values(payrollStats).map(s => [
            s.name,
            s.totalHours,
            s.paniers,
            s.overtime
        ]);

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `recap_paie_${months[selectedMonth]}_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderTimesheetCard = (ts) => {
        // GPS Verification Logic (Check all items in the day group)
        let distanceText = null;
        let isGpsOk = true;
        const allItems = ts.items || [ts];

        allItems.forEach(item => {
            if (item.description?.includes('GPS:')) {
                const gpsMatch = item.description.match(/GPS: ([\d.-]+), ([\d.-]+)/);
                if (gpsMatch && item.expand?.chantier) {
                    const workerLat = parseFloat(gpsMatch[1]);
                    const workerLng = parseFloat(gpsMatch[2]);
                    const cCoords = projectCoords[item.expand.chantier.id];

                    if (cCoords) {
                        const distKm = calculateDistance(workerLat, workerLng, cCoords.lat, cCoords.lng);
                        const distM = Math.round(distKm * 1000);
                        if (!distanceText || distM > 0) { // Keep most relevant or first
                            distanceText = `${distM}m du chantier`;
                        }
                        if (distM > 500) isGpsOk = false;
                    }
                }
            }
        });

        return (
            <div key={ts.id || ts.date} className={styles.timesheetCardPremium} style={{
                borderLeft: `5px solid ${ts.status === 'approved' ? '#10b981' : ts.status === 'rejected' ? '#ef4444' : ts.status === 'litige' ? '#8b5cf6' : '#f59e0b'}`,
                opacity: ts.status === 'approved' ? 0.8 : 1
            }}>
                <div className={styles.cardTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div>
                            {viewMode === 'list' && <div style={{ fontWeight: '800', fontSize: '0.9rem' }}>{ts.expand?.worker?.name}</div>}
                            <div className={styles.dateBadge}>
                                <FaCalendarAlt size={10} style={{ marginRight: '5px', opacity: 0.5 }} />
                                {new Date(ts.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </div>
                        </div>
                    </div>
                    <div className={styles.hourValue}>{ts.hours}h</div>
                </div>

                <div className={styles.cardContent}>
                    <div className={styles.projectTag}>
                        <FaProjectDiagram />
                        <span>{ts.expand?.chantier?.name || ts.items?.[0]?.expand?.chantier?.name || 'Général'}</span>
                    </div>

                    {ts.description && (
                        <div className={styles.descriptionBox}>
                            {ts.description}
                        </div>
                    )}

                    {ts.items?.length > 1 && (
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '5px' }}>
                            Cumul de {ts.items.length} saisies sur la journée
                        </div>
                    )}
                </div>

                <div className={styles.cardBadges}>
                    {ts.description?.includes('chronométrée') || ts.description?.includes('Session') ? (
                        <span className={styles.sourceBadge} style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>Chrono</span>
                    ) : (
                        <span className={styles.sourceBadge}>Saisie</span>
                    )}

                    {distanceText && (
                        <span className={styles.gpsIndicator} style={{
                            background: isGpsOk ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: isGpsOk ? '#10b981' : '#ef4444'
                        }}>
                            {isGpsOk ? <FaMapMarkerAlt /> : <FaExclamationCircle />}
                            {distanceText}
                        </span>
                    )}

                    {ts.is_corrected && (
                        <span className={styles.sourceBadge} style={{ background: 'rgba(164,196,57,0.1)', color: 'var(--color-primary)' }}>Corrigé</span>
                    )}

                    {ts.status === 'litige' && (
                        <span className={styles.sourceBadge} style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', fontWeight: 800 }}>RÉCLAMATION</span>
                    )}
                </div>

                <div className={styles.cardActionsRow} style={{ marginTop: '15px' }}>
                    {ts.status !== 'approved' && ts.status !== 'rejected' ? (
                        <>
                            <button className={styles.quickValidBtn} onClick={() => handleStatusUpdate(ts, 'approved')}>
                                <FaCheck /> Valider Journée
                            </button>
                            <button className={styles.rejectBtn} onClick={() => handleStatusUpdate(ts, 'rejected')}>
                                <FaTimes />
                            </button>
                        </>
                    ) : (
                        <div style={{ flex: 1 }} />
                    )}

                    <button className={styles.modeBtn} onClick={() => openEditModal(ts.items?.[0] || ts)} title="Corriger manuellement">
                        <FaEdit />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.tabContent}>
            {/* 1. PAYROLL PERIOD CONTROLS */}
            <div className={styles.validationHeaderMain}>
                <div>
                    <h2 className={styles.contentTitle} style={{ margin: 0 }}>Bureau : Validation RH</h2>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            className={styles.periodSelect}
                        >
                            {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                        </select>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className={styles.periodSelect}
                        >
                            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                <div className={styles.payrollRecapPill}>
                    <div className={styles.recapItem}>
                        <span className={styles.recapLabel}>FICHES</span>
                        <span className={styles.recapValue}>{timesheets.length}</span>
                    </div>
                    <div className={styles.recapDivider} />
                    <div className={styles.recapItem}>
                        <span className={styles.recapLabel}>HEURES TOTALES</span>
                        <span className={styles.recapValue}>{Object.values(payrollStats).reduce((acc, s) => acc + parseFloat(s.totalHours), 0).toFixed(1)}h</span>
                    </div>
                </div>
            </div>

            {/* 2. PAYROLL SUMMARY TABLE (Prop 1) */}
            <div className={styles.card} style={{ padding: '0', overflow: 'hidden' }}>
                <div
                    className={styles.cardHeaderAction}
                    onClick={() => setExpandedRecap(!expandedRecap)}
                    style={{ padding: '20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.02)' }}
                >
                    <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaTable style={{ color: 'var(--color-primary)' }} /> Tableau Récapitulatif Paie ({months[selectedMonth]})
                    </h3>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <button className={styles.exportMiniBtn} onClick={(e) => { e.stopPropagation(); exportRecapToCSV(); }}>
                            <FaFileDownload /> CSV
                        </button>
                        {expandedRecap ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                </div>

                {expandedRecap && (
                    <div className={styles.tableResponsive}>
                        <table className={styles.payrollTable}>
                            <thead>
                                <tr>
                                    <th>Employé</th>
                                    <th>Heures Normales</th>
                                    <th>Paniers Repas</th>
                                    <th>Heures Sup' (v1)</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.values(payrollStats).map(s => (
                                    <tr key={s.id}>
                                        <td style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '15px' }}>
                                            <div className={styles.smallAvatar}>
                                                {s.avatar ? <img src={s.avatar} alt="" /> : s.name.charAt(0)}
                                            </div>
                                            <span style={{ fontWeight: 700 }}>{s.name}</span>
                                        </td>
                                        <td>{s.totalHours}h</td>
                                        <td><span className={styles.badgePanier}>{s.paniers}</span></td>
                                        <td style={{ color: parseFloat(s.overtime) > 0 ? '#3b82f6' : 'inherit', fontWeight: 700 }}>
                                            {s.overtime > 0 ? `+${s.overtime}h` : '--'}
                                        </td>
                                        <td>
                                            <span className={styles.statusPill} style={{
                                                background: s.items.every(i => i.status === 'approved') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                                color: s.items.every(i => i.status === 'approved') ? '#10b981' : '#f59e0b'
                                            }}>
                                                {s.items.every(i => i.status === 'approved') ? 'Tout Validé' : 'Partiel / En attente'}
                                            </span>
                                        </td>
                                        <td>
                                            {!s.items.every(i => i.status === 'approved') && (
                                                <button className={styles.bulkValidBtn} onClick={() => handleBulkApprove(s.id)}>
                                                    Tout Valider
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {Object.keys(payrollStats).length === 0 && (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>Aucune donnée pour cette période.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 3. DETAIL VIEW TOGGLES */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div className={styles.viewTabs}>
                    <button className={`${styles.viewTab} ${filter === 'pending' ? styles.viewTabActive : ''}`} onClick={() => setFilter('pending')}>
                        À Valider ({timesheets.filter(t => t.status !== 'approved' && t.status !== 'rejected').length})
                    </button>
                    <button className={`${styles.viewTab} ${filter === 'approved' ? styles.viewTabActive : ''}`} onClick={() => setFilter('approved')}>
                        Déjà Validés
                    </button>
                </div>

                <div className={styles.viewModeGroup}>
                    <button className={`${styles.modeBtn} ${viewMode === 'grouped' ? styles.modeBtnActive : ''}`} onClick={() => setViewMode('grouped')}><FaLayerGroup /></button>
                    <button className={`${styles.modeBtn} ${viewMode === 'list' ? styles.modeBtnActive : ''}`} onClick={() => setViewMode('list')}><FaList /></button>
                </div>
            </div>

            {/* 4. DETAIL LIST / GRID */}
            {loading ? (
                <div className={styles.loadingInner}>Chargement des détails...</div>
            ) : (
                <div className={styles.workerGroupsDetail}>
                    {viewMode === 'list' ? (
                        <div className={styles.timesheetGridModern}>
                            {Object.values(payrollStats).flatMap(s => s.dailyItems).sort((a, b) => b.date.localeCompare(a.date)).map(renderTimesheetCard)}
                        </div>
                    ) : (
                        Object.values(payrollStats).map(group => (
                            <div key={group.id} className={styles.workerSectionCard}>
                                <div className={styles.workerSectionHeader}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div className={styles.workerAvatar}>
                                            {group.avatar ? <img src={group.avatar} alt="" /> : group.name.charAt(0)}
                                        </div>
                                        <h3 style={{ margin: 0 }}>{group.name}</h3>
                                    </div>
                                    <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>{Object.keys(group.dailyGroups).length} jours actifs sur la période</span>
                                </div>
                                <div className={styles.timesheetGridModern}>
                                    {group.dailyItems.map(renderTimesheetCard)}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* --- ADMIN CORRECTION MODAL --- */}
            {isEditModalOpen && editingTs && (
                <div className={styles.modalOverlay}>
                    <div className={styles.messageModal} style={{ maxWidth: '600px' }}>
                        <div className={styles.modalHeader}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h3 className={styles.modalTitle}>Correction Admin</h3>
                                {editingTs.status === 'litige' && <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 8px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 900 }}>LITIGE</span>}
                            </div>
                            <button onClick={() => setIsEditModalOpen(false)} className={styles.closeModalBtn}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            {/* MINI MAP PREVIEW IF GPS DATA EXISTS */}
                            {(() => {
                                const gpsMatch = editingTs.description?.match(/GPS: ([\d.-]+), ([\d.-]+)/);
                                const cCoords = projectCoords[editingTs.chantier];
                                if (gpsMatch || cCoords) {
                                    const workerLat = gpsMatch ? parseFloat(gpsMatch[1]) : (cCoords?.lat);
                                    const workerLng = gpsMatch ? parseFloat(gpsMatch[2]) : (cCoords?.lng);

                                    return (
                                        <div style={{ height: '150px', borderRadius: '12px', overflow: 'hidden', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                                            <MapContainer center={[workerLat, workerLng]} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                {gpsMatch && <Marker position={[workerLat, workerLng]} />}
                                                {cCoords && (
                                                    <Marker position={[cCoords.lat, cCoords.lng]}>
                                                        <Popup>Chantier</Popup>
                                                    </Marker>
                                                )}
                                            </MapContainer>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {editingTs.status === 'litige' && editingTs.description?.includes('[RÉCLAMATION]') && (
                                <div style={{ background: '#f5f3ff', padding: '12px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #ddd6fe' }}>
                                    <div style={{ fontSize: '0.65rem', fontWeight: 900, color: '#7c3aed', textTransform: 'uppercase', marginBottom: '4px' }}>Demande de l'ouvrier 💬</div>
                                    <div style={{ fontSize: '0.85rem', color: '#5b21b6', fontStyle: 'italic' }}>
                                        {editingTs.description.split('---')[0].replace('[RÉCLAMATION] :', '').trim()}
                                    </div>
                                </div>
                            )}

                            <div style={{ marginBottom: '15px' }}>
                                <label className={styles.recapLabel} style={{ display: 'block', marginBottom: '5px' }}>Ouvrier</label>
                                <div style={{ fontWeight: 800 }}>{editingTs.expand?.worker?.name}</div>
                            </div>

                            <div className={styles.timeDecomposition} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
                                <div className={styles.timeBlock}>
                                    <label>Matin</label>
                                    <div className={styles.timeInputs}>
                                        <input type="time" value={editingTs.morning_start || ''} onChange={(e) => setEditingTs({ ...editingTs, morning_start: e.target.value })} />
                                        <span>-</span>
                                        <input type="time" value={editingTs.morning_end || ''} onChange={(e) => setEditingTs({ ...editingTs, morning_end: e.target.value })} />
                                    </div>
                                </div>
                                <div className={styles.timeBlock}>
                                    <label>A-M</label>
                                    <div className={styles.timeInputs}>
                                        <input type="time" value={editingTs.afternoon_start || ''} onChange={(e) => setEditingTs({ ...editingTs, afternoon_start: e.target.value })} />
                                        <span>-</span>
                                        <input type="time" value={editingTs.afternoon_end || ''} onChange={(e) => setEditingTs({ ...editingTs, afternoon_end: e.target.value })} />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '15px', marginTop: '15px' }}>
                                <div className={styles.formField}>
                                    <label>Heures Totales</label>
                                    <input type="number" step="0.5" value={editingTs.hours} onChange={(e) => setEditingTs({ ...editingTs, hours: e.target.value })} />
                                </div>
                                <div className={styles.formField}>
                                    <label>Chantier</label>
                                    <select value={editingTs.chantier} onChange={(e) => setEditingTs({ ...editingTs, chantier: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: '10px' }}>
                                        <option value="">Sélectionner...</option>
                                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formField}>
                                <label>Notes / Correction</label>
                                <textarea
                                    value={editingTs.description || ''}
                                    onChange={(e) => setEditingTs({ ...editingTs, description: e.target.value })}
                                    placeholder="Expliquez la modification..."
                                    style={{ height: '80px' }}
                                />
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.viewBtn} onClick={() => setIsEditModalOpen(false)}>Annuler</button>
                            <button className={styles.addButton} onClick={handleSaveCorrection} disabled={isSaving}>
                                {isSaving ? "Enregistrement..." : "Enregistrer et Valider"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabTimesheetAdmin;
